import { Action, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { CampaignsState } from 'lib/store/campaigns/types';
import { DeliveryAreasState } from 'lib/store/deliveryAreas/reducer';
import { NotificationsState } from 'lib/store/notifications/reducer';
import { PermissionsState } from 'lib/store/permissions/reducer';
import { RestaurantsState } from 'lib/store/restaurants/reducer';
import { UserState } from 'lib/store/users/reducer';
import { RewardsState } from 'lib/store/rewards/reducer';
import { SessionState } from 'lib/store/session/reducer';
import { OffersState } from 'lib/store/offers/types';
import { DataFiltersState } from 'lib/store/dataFilters/reducer';
import { IntercomState } from 'lib/store/intercom/types';
import { BankingDetailsState } from 'lib/store/banking/types';
import { DeliveryAreasAction } from 'lib/store/deliveryAreas/actions';
import { NotificationsAction } from 'lib/store/notifications/actions';
import { RestaurantAction } from 'lib/store/restaurants/actionTypes';
import { SessionAction } from 'lib/store/session/actions';
import { NewOfferAction, OfferAction } from 'lib/store/offers/actions';
import { DataFiltersAction } from 'lib/store/dataFilters/actions';
import { SignupState } from 'lib/store/signup/types';
import { IntegrationsState } from 'lib/store/integrations/types';
import I18n from 'lib/utils/i18n';
import { OpeningHoursState } from 'lib/store/openingHours/reducer';
import { MetricsState } from 'lib/store/metrics/reducer';
import { ReportingPlatformState } from 'lib/store/reportingPlatform/types';
import { ConnectedModalState } from 'lib/store/connectedModal/types';
import { CampaignsAction } from 'lib/store/campaigns/actions';
import { ReviewsState } from 'lib/store/reviews/types';
import { MarketerState } from 'lib/store/marketer/reducer';
import { DaysOffState } from 'lib/store/daysOff/reducer';
import { FleetSwitchingState } from 'lib/store/fleetSwitching/reducer';
import { RefundDisputesState } from 'lib/store/refundDisputes/reducer';
import { RefundDisputesAction } from 'lib/store/refundDisputes/actions';
import {
  APIGWAllowedListState,
  APIGWAllowedListAction,
} from 'lib/store/apiGateway/actions';
import { SiteStatusState } from 'lib/store/siteStatus/reducer';

// Utility types
export type ValueOf<T> = T[keyof T];
// Filters out `undefined`. Doesn't filter out `null`
export type Defined<T> = T extends void ? never : T;

// Global models

export interface Permission {
  url: string;
  methods: string[];
}

export interface Permissions {
  [k: string]: Permission | Permissions;
}

export interface User {
  id: string;
  drnId: string;
  drn: string;
  role: string;
  name: string;
  email: string;
  isSso: boolean;
  userJoinedAt: string;
  links: Permissions;
  features: string[];
  branchIds: string[];
  orgIds: string[];
}

export type ODCUser = {
  anonymizedUserId: string;
  name: string;
  username: string;
  role: string;
  branchDrn: string;
  branchDrnId: string;
  securityPrincipalId: string;
  securityPrincipalDrn: string;
  market: string;
};

export const BranchBusinessModel = {
  HOP_DELIVEROO_MANAGED: 'HOP_DELIVEROO_MANAGED',
  HOP_PARTNER_MANAGED: 'HOP_PARTNER_MANAGED',
} as const;
export type BranchBusinessModelValue = ValueOf<typeof BranchBusinessModel>;

export interface RestaurantBranch {
  id: string;
  drnId: string;
  shortDrn: string;
  name: string;
  orgId: string;
  orgShortDrn: string;
  orgDrnId: string;
  zoneOffset: number;
  zoneId: string;
  timeZone: string;
  location: {
    lat: number;
    lng: number;
  };
  features: string[];
  fulfillmentTypes: string[];
  branchType: string;
  isActive: boolean;
  customerFacingLink: string;
  hygieneRating: string | null;
  hygieneRatingLastUpdated: Date | null;
  hygieneRatingScheme: string;
  isCustomerChampion: boolean;
  branchBusinessModel: BranchBusinessModelValue | null;
  city: string;
  isMarketplacePlusRestaurant: boolean;
  countryCode: string;
  status: string;
}

export interface RestaurantOrg {
  shortDrn: string;
  id: string;
  drnId: string;
  name: string;
  countryCode: string;
  languageCode: string;
  features: string[];
  branches: RestaurantBranch[];
}

export interface Amount {
  currencyCode: string;
  fractional?: number;
}

export interface Notification {
  title: string;
  subtitle?: string;
  autoCloseTimeoutMs?: number;
}

export interface Error {
  status: number;
  error: string;
}

export interface FiltersQueryParams {
  orgId?: string;
  branchId?: string;
}

// Redux
export interface AppState {
  campaigns: CampaignsState;
  rewards: RewardsState;
  deliveryAreas: DeliveryAreasState;
  notifications: NotificationsState;
  permissions: PermissionsState;
  restaurants: RestaurantsState;
  session: SessionState;
  offers: OffersState;
  openingHours: OpeningHoursState;
  orders: Partial<Record<string, any>>;
  metrics: MetricsState;
  dataFilters: DataFiltersState;
  intercom: IntercomState;
  banking: BankingDetailsState;
  signup: SignupState;
  integrations: IntegrationsState;
  reportingPlatform: ReportingPlatformState;
  connectedModal: ConnectedModalState;
  reviews: ReviewsState;
  marketer: MarketerState;
  daysOff: DaysOffState;
  users: UserState;
  fleetSwitching: FleetSwitchingState;
  refundDisputes: RefundDisputesState;
  siteStatus: SiteStatusState;
  request: Partial<Record<string, any>>;
  apiGWAllowedList: APIGWAllowedListState;
}

// this creates a transitive circular dependency
// Should use a generic instead
export type AppAction =
  | DeliveryAreasAction
  | NotificationsAction
  | RestaurantAction
  | SessionAction
  | NewOfferAction
  | OfferAction
  | DataFiltersAction
  | CampaignsAction
  | RefundDisputesAction
  | APIGWAllowedListAction;

interface ConvertingApiOptions<T> extends Omit<RequestInit, 'body'> {
  body?: T;
}

interface ApiOptions extends RequestInit {
  body?: any;
}

type ActionData = { convertCase?: boolean } & Record<string, any>;

export interface InputAction<T> extends Action<T> {
  payload: string;
}

export interface ApiAction<T> extends Action<T> {
  endpoint: string;
  extra?: ActionData;
  options?: ApiOptions;
}

/**
 * enforces conversion of `snake_case` objects to `camelCase`
 */
export type ApiDataAction<T, P = never> = Action<T> & {
  endpoint: string;
  extra: { convertCase: true } & Omit<ActionData, 'convertCase'>;
  options?: ConvertingApiOptions<P>;
};

export interface ApiRequestAction<T> extends Action<T> {
  extra: ActionData;
}

export interface ApiSuccessAction<T, P = undefined>
  extends Action<T extends Action ? `${T['type']}_SUCCESS` : T> {
  extra: ActionData;
  status: number;
  headers: HeadersInit;
  payload: P;
}

export interface ApiFailureAction<T, P = Error>
  extends Action<T extends Action ? `${T['type']}_FAILURE` : T> {
  extra: ActionData;
  status: number;
  payload?: P;
}

export type ApiDispatch = <
  ActionType extends ApiAction<Action['type']>,
  ResultType extends ApiSuccessAction<Action['type'], any>,
>(
  action: ActionType,
) => Promise<ResultType>;

export interface AppDispatch extends ApiDispatch, Dispatch {}

export interface ThunkContext {
  i18n: I18n;
}

export type ThunkResult<R = void> = ThunkAction<
  R,
  AppState,
  ThunkContext,
  AppAction
>;

export interface RestaurantDayOff {
  referenceKey: string;
  startDate: Date;
  endDate: Date;
  description: string;
  branchIds: string[];
  daysOffIds: string[];
}

export interface APIGWAllowedList {
  apiGWAllowedList: string[];
}
